import { Button, ButtonColor, ButtonSize, Table, TableRow } from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { CallCenterInterbank } from 'src/front/api/apiInstance';
import { CallCenterInterbankMoneyTransferHistoryResponseData } from 'src/front/api/generated';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import { TransferHistorySchema } from 'src/front/common/utils/validationSchemas';
import Datepicker from 'src/front/components//Datepicker';
import {
  Form,
  FormElement,
  FormRow,
  InfoLabel,
  InfoRow,
  Separator,
} from 'src/front/components/FormStyles';
import { TABLE_WIDTHS } from 'src/front/constant';
import { useUserInfoState } from 'src/front/contexts/SessionContext';
import { useAccountState } from 'src/front/contexts/TransferHistoryContext';
import styled from 'styled-components';
import { CallCenterTableWrapper } from './AccountSelectionForm';

const CornerFormElement = styled.div`
  display: flex;
  justify-content: flex-end;

  margin-top: 15px;
`;
const tableHeaders = [
  'general_components_paymentDate_text',
  'general_components_creationTime_text',
  'general_status_label',
  'general_documentNumber_label',
  'general_components_direction_text',
  'general_components_product_text',
  'general_components_amount_text',
  'general_components_recipientAccount_text',
  'general_components_bicRecipient_text',
  'general_components_customerComment_text',
];
export interface HistoryFormData {
  beginDate: Date | null;
  endDate: Date | null;
}
const defaultValues = {
  beginDate: null,
  endDate: null,
};
const HistoryForm: FC = () => {
  const { accountValues } = useAccountState();
  const { t } = useTranslation();
  const { processDate } = useUserInfoState();
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [historyList, setHistoryList] = useState<
    CallCenterInterbankMoneyTransferHistoryResponseData[]
  >([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyPage, setHistoryPage] = useState<number>(1);
  const [showResult, setShowResult] = useState<boolean>(false);
  const resolver = useYupResolver(TransferHistorySchema, { todayMaxControl: true });
  const { watch, register, errors, getValues, setValue, handleSubmit, formState } = useForm<
    HistoryFormData
  >({
    defaultValues: {
      ...defaultValues,
      beginDate: processDate || null,
      endDate: processDate || null,
    },
    resolver,
  });

  useEffect(() => {
    register({ name: 'beginDate' });
    register({ name: 'endDate' });
  }, [register]);

  const loadHistoryList = useCallback(
    async (pageNumber = historyPage, forceRefresh = false) => {
      setHistoryLoading(true);
      const { beginDate, endDate } = getValues();
      const { data } = await CallCenterInterbank.getMoneyTransferHistory({
        pageNumber,
        refresh: !pageNumber || forceRefresh,
        accountNo: accountValues?.accountNumber || '',
        endDate: formatDate(endDate),
        startDate: formatDate(beginDate),
        rowCountPerPage: 20,
      });
      if (data.result) {
        setHistoryList(data.transferList);
        setHasNextPage(data.hasNextPage);
        setHistoryPage(pageNumber || 1);
      }
      setHistoryLoading(false);
    },
    [accountValues, getValues, historyPage]
  );
  const handleDateChange = (fieldName: string) => (date: Date): void =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    setValue(fieldName, date, {
      shouldValidate: formState.isSubmitted || !!date,
      shouldDirty: formState.isSubmitted || !!date,
    });
  const onSearchSubmit = (): void => {
    loadHistoryList(1, true);
    setShowResult(true);
  };
  const transferList: TableRow[] = historyList.map((transfer, index) => ({
    id: index.toString(),
    columns: [
      transfer.paymentDate,
      transfer.creationTime,
      transfer.status,
      transfer.documentNumber,
      transfer.direction,
      transfer.product,
      transfer.amount,
      transfer.recipientAccount,
      transfer.bicRecipent,
      transfer.customerComment,
    ],
  }));
  return (
    <>
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="general_components_accountNumber_text" />:
        </InfoLabel>
        {accountValues?.accountNumber}
      </InfoRow>
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="general_currency_label" />:
        </InfoLabel>
        {accountValues?.currency}
      </InfoRow>
      <Separator />
      <form onSubmit={handleSubmit(onSearchSubmit)}>
        <FormRow>
          <FormElement>
            <Datepicker
              errorMessage={t(errors?.beginDate?.message as string)}
              inputLabel={`${t('general_components_startDate_label')} *`}
              onChange={handleDateChange('beginDate')}
              value={watch('beginDate') as Date}
            />
          </FormElement>
          <FormElement>
            <Datepicker
              errorMessage={t(errors?.endDate?.message as string)}
              inputLabel={`${t('general_components_endDate_label')} *`}
              onChange={handleDateChange('endDate')}
              value={watch('endDate') as Date}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <FormRow>
          <FormElement />
          <CornerFormElement>
            <Button
              color={ButtonColor.Primary}
              disabled={formState.isSubmitting}
              size={ButtonSize.Medium}
              text={t('general_components_search_button')}
              type="submit"
            />
          </CornerFormElement>
        </FormRow>
      </form>
      {showResult ? (
        <>
          <Form>
            <CallCenterTableWrapper>
              <Table
                emptyTableText={t('general_components_notFound_errorMessage')}
                hasNextPage={hasNextPage}
                headers={tableHeaders.map((head) => t(head))}
                items={transferList}
                loading={historyLoading}
                loadPageItems={loadHistoryList}
                pageNumber={historyPage}
                tableWidth={TABLE_WIDTHS.DESKTOP_2X}
                widths={['10', '10', '10', '10', '10', '10', '10', '10', '10', '10']}
                clearItemOnPageChange
              />
            </CallCenterTableWrapper>
          </Form>
        </>
      ) : null}
    </>
  );
};
export default HistoryForm;
